import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import BannerCreateModal from './BannerCreateModal';
import { isEmployer } from '../lib/roleHelpers';

/**
 * Dismissible announcement introducing the homepage banner feature to employers.
 * Mirrors the non-blocking floating-banner PATTERN used by LanguageSelectionModal,
 * but with a DISTINCT localStorage key (`banner_intro_shown_count`) so dismissing/
 * using one never affects the other. Employer-only; shown up to `maxShows` times
 * per browser (admin-configurable via banner_popup_max_shows), then goes dormant.
 */
const BannerIntroModal = ({ profile, maxShows = 3 }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (!isEmployer(profile)) return undefined;
    try {
      const rawCount = localStorage.getItem('banner_intro_shown_count');
      const shownCount = parseInt(rawCount, 10) || 0;
      if (shownCount < maxShows) {
        const showTimer = setTimeout(() => {
          try {
            localStorage.setItem('banner_intro_shown_count', String(shownCount + 1));
          } catch {
            // localStorage blocked; still show this once, just won't persist the count
          }
          setShow(true);
        }, 1200);
        return () => clearTimeout(showTimer);
      }
    } catch {
      // localStorage blocked, don't show
    }
    return undefined;
  }, [profile, maxShows]);

  const dismiss = () => {
    setShow(false);
  };

  const handleCta = () => {
    setShow(false);
    setShowCreateModal(true);
  };

  if (!show && !showCreateModal) return null;

  return (
    <>
      {show && (
        <div className="fixed bottom-2 left-2 right-2 z-[100] md:left-auto md:right-4 md:bottom-4 md:w-96 animate-in slide-in-from-bottom duration-300">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {t('banner.introTitle')}
              </p>
              <Button
                onClick={dismiss}
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                aria-label={t('banner.introDismiss')}
              >
                <X size={14} />
              </Button>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              {t('banner.introBody')}
            </p>
            <div className="flex gap-2">
              <Button onClick={handleCta} size="sm" className="flex-1 font-semibold" data-testid="banner-intro-cta">
                {t('banner.introCta')}
              </Button>
              <Button onClick={dismiss} variant="outline" size="sm" className="flex-1">
                {t('banner.introDismiss')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <BannerCreateModal onClose={() => setShowCreateModal(false)} />
      )}
    </>
  );
};

export default BannerIntroModal;
